import localDB from '../crud/localDB/localDB';

// Default values
const DEFAULT_FONT_SIZE_PX = 13.5; // matches index.scss initial
const MIN_FONT_SIZE_PX = 12;
const MAX_FONT_SIZE_PX = 18;

const DEFAULT_APP_ZOOM = 1.0;
const MIN_APP_ZOOM = 0.8;
const MAX_APP_ZOOM = 1.4;

function clamp(n: number, min: number, max: number) {
  return Math.min(max, Math.max(min, n));
}

/**
 * Apply base font size to :root so all rem units follow it.
 */
export function applyBaseFontSize(px: number) {
  const clamped = clamp(px, MIN_FONT_SIZE_PX, MAX_FONT_SIZE_PX);
  document.documentElement.style.fontSize = `${clamped}px`;
}

/**
 * Apply global zoom using CSS variable on html/body that components can consume
 * or as a fallback, set CSS zoom style (supported in Chromium, partial elsewhere).
 */
export function applyAppZoom(factor: number) {
  const clamped = clamp(factor, MIN_APP_ZOOM, MAX_APP_ZOOM);
  document.documentElement.style.setProperty('--app-zoom', String(clamped));
  (document.body.style as any).zoom = String(clamped);
}

/** Load preferences from LocalDB and apply them ASAP. */
export async function initUiPreferences() {
  try {
    const [fontPx, zoom] = await Promise.all([
      localDB.get<number>('ui', 'baseFontSizePx'),
      localDB.get<number>('ui', 'appZoom'),
    ]);
    applyBaseFontSize(fontPx ?? DEFAULT_FONT_SIZE_PX);
    applyAppZoom(zoom ?? DEFAULT_APP_ZOOM);
  } catch (e) {
    // On any failure, still apply defaults
    applyBaseFontSize(DEFAULT_FONT_SIZE_PX);
    applyAppZoom(DEFAULT_APP_ZOOM);
  }
}

export async function setUiBaseFontSize(px: number) {
  const clamped = clamp(px, MIN_FONT_SIZE_PX, MAX_FONT_SIZE_PX);
  await localDB.set('ui', 'baseFontSizePx', clamped);
  applyBaseFontSize(clamped);
}

export async function setUiAppZoom(factor: number) {
  const clamped = clamp(factor, MIN_APP_ZOOM, MAX_APP_ZOOM);
  await localDB.set('ui', 'appZoom', clamped);
  applyAppZoom(clamped);
}

export const uiPrefBounds = {
  font: { min: MIN_FONT_SIZE_PX, max: MAX_FONT_SIZE_PX, def: DEFAULT_FONT_SIZE_PX },
  zoom: { min: MIN_APP_ZOOM, max: MAX_APP_ZOOM, def: DEFAULT_APP_ZOOM },
};
